import { createReducer, on } from '@ngrx/store';
import { IResponseError } from '../../service/interface';
import {
	IFactcheckSearchProvider,
	IFactcheckSearchRouting,
} from '../../service/factcheck-search-provider/interface';
import { FactcheckSearchProviderActions } from './factcheck-search-provider.actions';

export const factcheckSearchProviderFeatureKey = 'factcheckSearchProvider';

export interface IState {
	providers: IFactcheckSearchProvider[];
	languages: string[];
	routing: IFactcheckSearchRouting | null;
	loading: boolean;
	pendingChanges: number;
	error?: IResponseError;
}

export const initialState: IState = {
	providers: [],
	languages: [],
	routing: { languages: [], others: [] },
	loading: false,
	pendingChanges: 0,
};

export const reducer = createReducer(
	initialState,
	on(
		FactcheckSearchProviderActions.loadProviders,
		(state): IState => ({ ...state, loading: true, error: undefined }),
	),
	on(
		FactcheckSearchProviderActions.reloadProviders,
		(state): IState => ({ ...state, loading: true }),
	),
	on(
		FactcheckSearchProviderActions.providersReceived,
		(state, { settings }): IState =>
			// The last save's answer will carry the final state; this read may predate queued clicks
			state.pendingChanges > 0
				? { ...state, loading: false }
				: {
						...state,
						providers: settings.providers,
						languages: settings.languages,
						routing: settings.routing,
						loading: false,
					},
	),
	on(
		FactcheckSearchProviderActions.changeSaved,
		(state, { settings }): IState => {
			const pendingChanges = state.pendingChanges - 1;
			// An earlier answer predates the queued clicks, so it would undo them on screen
			return pendingChanges > 0
				? { ...state, pendingChanges }
				: {
						...state,
						pendingChanges: 0,
						providers: settings.providers,
						languages: settings.languages,
						routing: settings.routing,
					};
		},
	),
	on(
		FactcheckSearchProviderActions.loadProvidersError,
		(state, { error }): IState => ({ ...state, loading: false, error }),
	),
	on(
		FactcheckSearchProviderActions.changeError,
		(state, { error }): IState => ({
			...state,
			loading: false,
			error,
			pendingChanges: Math.max(state.pendingChanges - 1, 0),
		}),
	),
	on(
		FactcheckSearchProviderActions.updateProvider,
		(state, { provider, changes }): IState => ({
			...state,
			error: undefined,
			pendingChanges: state.pendingChanges + 1,
			providers: state.providers.map((current) =>
				current.provider === provider ? { ...current, ...changes } : current,
			),
		}),
	),
	on(
		FactcheckSearchProviderActions.reorderProviders,
		(state, { providers }): IState => ({
			...state,
			error: undefined,
			pendingChanges: state.pendingChanges + 1,
			providers: providers
				.flatMap((name) =>
					state.providers.filter((current) => current.provider === name),
				)
				.map((current, index) => ({
					...current,
					priority: state.providers[index].priority,
				})),
		}),
	),
);
